import { ESdChangeReason, IApiManagement, IApiManagementDto, IApiMethods } from "@kuika/kuika-cl-model";
import { Form, FormInstance } from "antd/es";
import { Guid } from "guid-typescript";
import _ from "lodash";
import * as React from "react";
import { IWithSDProps, withSD } from "../../../shared/hoc/screen-design-consumer";
import { PureSdComponent } from "../../../shared/pure-sd";
import { getSdInstance } from "../../../shared/utility/designer-context";
import { SaveStatus } from "../../../shared/utility/save-status";
import { DeleteModal } from "../../common/delete-modal";
import KuikaBadge from "../../ui-kit/kuika-badge/kuika-badge";
import { type KuikaButtonStatus } from "../../ui-kit/kuika-button/kuika-button";
import KuikaInput from "../../ui-kit/kuika-input/kuika-input";
import KuikaListPage, {
  KuikaCreateCard,
  KuikaCreateCardRow,
  KuikaListPageToolbar
} from "../../ui-kit/kuika-list-page/kuika-list-page";
import KuikaListTable, {
  KuikaListTableColumn,
  KuikaListTableNameCell,
  KuikaListTableRowAction,
  KuikaListTableStatusCell
} from "../../ui-kit/kuika-list-table/kuika-list-table";
import KuikaSvg from "../../ui-kit/kuika-svg/kuika-svg";
import KuikaSmallText from "../../ui-kit/typography/SmallText/SmallText";
import {
  filterMethods,
  getApiMethods,
  IApiMethodAuth,
  IApiMethodVerb,
  methodAuth,
  methodInputCount,
  methodVerb
} from "./api-builder-model";
import { ApiEditorHeader } from "./api-editor-header";
import { ApiMethodModal } from "./api-method-modal";
import "./api-builder.scss";

/**
 * One API's methods — the API Builder's second surface, reached by opening a row on the
 * landing and left through the header's back control.
 *
 * The two levels are two surfaces rather than one folder-style tree because an API is not
 * a folder: it owns a rename control, and four of the five method columns (verb, access,
 * actions, inputs) are blank on an API row. Methods also cannot move between APIs — they
 * live inside `api.json` and every write is a whole-document PUT — so the drag-and-drop a
 * tree exists for has nothing to do here.
 *
 * Clicking a method selects it (`sd.setActiveMethod`), which is what opens the right
 * panel's method editor. This page shows a method's verb and access; it never edits them.
 *
 * Method writes live here rather than in the canvas because they rewrite the API document
 * this surface is already holding, and the surface stays mounted while its modals are up.
 */

export interface IApiEditorProps {
  /** Leaving the editor, routed through the host's close path. */
  onBack: () => void;
  /** The API list's own row actions, for the switcher's "…". */
  rowActions: KuikaListTableRowAction<IApiManagement>[];
  onSwitchApi: (api: IApiManagement) => void;
  onCreateNew: () => void;
}

interface IApiEditorState {
  searchText: string;
  isMethodModalOpen: boolean;
  /** Where the method sheet's save stands. Owned by `SaveStatus`, never set directly. */
  methodStatus: KuikaButtonStatus;
  isNameOnEdit: boolean;
  methodToDelete?: IApiMethods;
  busyMethodId?: string;
  /** The method the dialog just saved, revealed in the table it closes onto. */
  revealMethodId: string | null;
}

class ApiEditor extends PureSdComponent<IApiEditorProps & IWithSDProps, IApiEditorState> {
  formRef = React.createRef<FormInstance>();

  /**
   * The method sheet's state machine: the ✓ is held on Save, then the sheet closes onto the
   * methods table and the saved row is revealed. `canStart` keeps an Enter pressed during the ✓
   * from saving the method a second time.
   */
  /** Set once the editor has closed; a save still in flight then must not reopen it. */
  private isUnmounted: boolean = false;

  private readonly methodStatus: SaveStatus = new SaveStatus((methodStatus: KuikaButtonStatus): void => {
    this.setState({ methodStatus });
  });

  constructor(props: IApiEditorProps & IWithSDProps) {
    super(props);
    this.state = {
      searchText: "",
      isMethodModalOpen: false,
      methodStatus: "idle",
      isNameOnEdit: false,
      revealMethodId: null
    };
  }

  componentWillUnmount(): void {
    this.isUnmounted = true;
    this.methodStatus.dispose();
  }

  componentDidUpdate(prevProps: IApiEditorProps & IWithSDProps): void {
    // Switching APIs re-seeds the rename field and drops any half-finished rename.
    if (this.props.sd.selectedApiId !== prevProps.sd.selectedApiId) {
      this.resetNameField();
    }
  }

  getApi = (): IApiManagement | undefined => this.props.sd.getSelectedApi();

  getApis = (): IApiManagement[] => this.props.sd?.apiManagements ?? [];

  getMethods = (): IApiMethods[] => getApiMethods(this.getApi());

  getEditingMethod = (): IApiMethods | undefined =>
    this.getMethods().find((method: IApiMethods): boolean => method.id === this.props.sd.editingApiMethodId);

  //  ── Rename ─────────────────────────────────────────────────────────────────

  resetNameField = (): void => {
    this.formRef.current?.setFieldsValue({ name: this.getApi()?.name ?? "" });
    this.setState({ isNameOnEdit: false });
  };

  existingNameValidator = async (_rule: unknown, value: string): Promise<void> => {
    if (!value) return;
    const currentId: string | undefined = this.getApi()?.id;
    const clash: boolean = this.getApis().some(
      (api: IApiManagement): boolean => api.name?.toLowerCase() === value.toLowerCase() && api.id !== currentId
    );
    if (clash) throw new Error("This API Name is used before.");
  };

  /**
   * An API named like a platform-reserved key (e.g. "Kuika") collides with the generated
   * root namespace and breaks code generation, so the rename is rejected before it lands.
   */
  reservedNameValidator = async (_rule: unknown, value: string): Promise<void> => {
    if (!value) return;
    const reserved: string[] = (this.props.sd.project?.commonReservedKeys ?? []).map((key: string): string =>
      key.toLowerCase()
    );
    if (reserved.includes(value.toLowerCase())) {
      throw new Error(`"${value}" is a reserved name. Please use another name.`);
    }
  };

  validateName = async (): Promise<string | undefined> => {
    try {
      const values = await this.formRef.current?.validateFields(["name"]);
      return values?.name;
    } catch {
      return undefined;
    }
  };

  handleRenameSubmit = async (name: string): Promise<void> => {
    const api: IApiManagement | undefined = this.getApi();
    if (!api) return;
    this.setState({ isNameOnEdit: false });
    if (name === api.name) return;
    await this.saveApi(api, { name });
  };

  //  ── Writes ─────────────────────────────────────────────────────────────────

  /**
   * Every write is the whole API document: methods live inside `json`, so saving one
   * method saves the API. `saveApiManagements` reloads the list afterwards.
   *
   * `json` is handed over as an OBJECT, never a string. The provider stringifies what is
   * not already a string, so a caller that stringifies early is merely relying on that
   * check — and the day it changes, a double-encoded `"\"{}\""` silently loses every
   * method. One shape, one stringifier.
   */
  saveApi = async (api: IApiManagement, changes: Partial<IApiManagementDto>): Promise<void> => {
    const payload = {
      id: api.id,
      name: api.name,
      kProject_Id: this.props.sd.project?.id,
      json: api.json,
      xml: api.xml ?? "api",
      dynamicInput: api.dynamicInput,
      ...changes
    } as IApiManagementDto;

    await this.props.sd.saveApiManagements(payload);
    // Re-selecting the API after the list reloads keeps an open editor on it. Once the user has
    // left for the list, the same call would pull them back into the API they just left.
    if (this.isUnmounted) return;
    this.props.sd.setActiveApi(api.id);
  };

  openCreateMethodModal = (): void => {
    this.props.sd.cancelEditingMethod();
    this.setState({ isMethodModalOpen: true });
  };

  openEditMethodModal = (method: IApiMethods): void => {
    this.props.sd.setEditingMethod(method.id);
    this.setState({ isMethodModalOpen: true });
  };

  closeMethodModal = (): void => {
    this.props.sd.setEditingMethod(undefined);
    this.setState({ isMethodModalOpen: false });
  };

  handleMethodSubmit = async (name: string, description?: string): Promise<void> => {
    const api: IApiManagement | undefined = this.getApi();
    if (!api || !this.methodStatus.canStart) return;
    const existing: IApiMethods | undefined = this.getEditingMethod();

    const method = {
      // Editing keeps the method's id, and with it everything the right panel configured:
      // actions, inputs, verb, content types and authorization.
      ...(existing ?? {}),
      id: existing?.id ?? Guid.create().toString(),
      name,
      description,
      actions: existing?.actions ?? [],
      inputMethods: existing?.inputMethods ?? []
    } as IApiMethods;

    const json = _.cloneDeep(api.json) ?? {};
    if (!json.methods) {
      json.methods = [];
    }
    const index: number = json.methods.findIndex((item: IApiMethods): boolean => item.id === method.id);
    if (index === -1) {
      json.methods.push(method);
    } else {
      json.methods.splice(index, 1, method);
    }

    this.methodStatus.start();
    try {
      await this.saveApi(api, { json });
      // The dialog closes onto the methods table once its ✓ has been seen; the row it saved is
      // where the eye is taken.
      this.methodStatus.succeed((): void => {
        this.closeMethodModal();
        this.setState({ revealMethodId: method.id });
      });
    } catch {
      // `saveApiManagements` has already said what went wrong; the sheet stays open on "Try again".
      this.methodStatus.fail();
    }
  };

  requestDeleteMethod = (method: IApiMethods): void => {
    this.setState({ methodToDelete: method });
  };

  /**
   * Deleting a method rewrites the API document and reports the removed actions to the
   * StartProcess trigger sync — both carried over from the list this page replaces.
   */
  deleteMethod = async (method: IApiMethods): Promise<void> => {
    const api: IApiManagement | undefined = this.getApi();
    if (!api) return;

    const triggerSync = getSdInstance().startProcessTriggerSync;
    const previousSnapshot = triggerSync.snapshot(method.actions);

    const json = _.cloneDeep(api.json);
    const index: number = json?.methods?.findIndex((item: IApiMethods): boolean => item.id === method.id) ?? -1;
    if (index === -1) return;
    json.methods.splice(index, 1);

    this.setState({ busyMethodId: method.id });
    try {
      await this.saveApi(api, { json });
      if (this.props.sd.activeApiMethodId === method.id) {
        this.props.sd.cancelActiveMethod();
      }
      triggerSync.applyDiff({
        sourceType: "ApiManagement",
        sourceId: api.id,
        previousSnapshot,
        currentSnapshot: []
      });
    } finally {
      this.setState({ busyMethodId: undefined, methodToDelete: undefined });
    }
  };

  //  ── Table ──────────────────────────────────────────────────────────────────

  getColumns = (): KuikaListTableColumn<IApiMethods>[] => [
    {
      id: "name",
      header: () => "Method",
      headerTestId: "methodColumnText",
      accessorFn: (row: IApiMethods) => row.name?.toLowerCase() ?? "",
      cell: (row: IApiMethods) => (
        <span
          className="kuika-api-builder__name-cell"
          data-testid={`${row.name}Method`}
          style={
            {
              "--kuika-list-table-icon-bg": "var(--blue-1)",
              "--kuika-list-table-icon-color": "var(--accent-fg)"
            } as React.CSSProperties
          }
        >
          <KuikaListTableNameCell name={row.name} icon={<KuikaSvg name="code" width={14} height={14} />} />
        </span>
      )
    },
    {
      id: "httpMethod",
      header: () => "Verb",
      headerTestId: "httpMethodColumnText",
      width: 120,
      // The enum's own order, not the label: sorting alphabetically puts DELETE before
      // GET, which reads as random.
      accessorFn: (row: IApiMethods) => methodVerb(row).ordinal,
      cell: (row: IApiMethods) => {
        const verb: IApiMethodVerb = methodVerb(row);
        return <KuikaBadge type="label" size="small" color={verb.color} content={verb.label} />;
      }
    },
    {
      id: "authorization",
      header: () => "Access",
      headerTestId: "authorizationColumnText",
      width: 160,
      accessorFn: (row: IApiMethods) => methodAuth(row).ordinal,
      cell: (row: IApiMethods) => {
        const auth: IApiMethodAuth = methodAuth(row);
        // Editing a method's access changes this badge on the row the user just saved.
        return <KuikaListTableStatusCell label={auth.label} color={auth.color} hint={auth.hint} animateChanges />;
      }
    },
    {
      id: "actions",
      header: () => "Actions",
      headerTestId: "actionsColumnText",
      width: 110,
      align: "right",
      accessorFn: (row: IApiMethods) => row.actions?.length ?? 0,
      // Per-row counter ids, carried over from the right-panel methods list this table
      // replaced: the E2E suite reads a method's action/input count off the row a user
      // scans, not out of the panel that was just edited (QA-519).
      cell: (row: IApiMethods) => (
        <KuikaSmallText data-testid={`${row.name}MethodActionCount`}>{row.actions?.length ?? 0}</KuikaSmallText>
      )
    },
    {
      id: "inputs",
      header: () => "Method Inputs",
      headerTestId: "methodInputsColumnText",
      width: 150,
      align: "right",
      // What the right panel lists: a List of Objects method's fields, not its one wrapper input.
      accessorFn: (row: IApiMethods) => methodInputCount(row),
      cell: (row: IApiMethods) => (
        <KuikaSmallText data-testid={`${row.name}MethodInputCount`}>{methodInputCount(row)}</KuikaSmallText>
      )
    }
  ];

  getRowActions = (): KuikaListTableRowAction<IApiMethods>[] => [
    {
      key: "edit",
      label: "Edit",
      icon: <KuikaSvg width={16} height={16} name="edit" />,
      dataTestId: "methodRenameButton",
      onSelect: (row: IApiMethods) => this.openEditMethodModal(row)
    },
    {
      key: "delete",
      label: "Delete",
      icon: <KuikaSvg width={16} height={16} name="trash" />,
      danger: true,
      dataTestId: "methodDeleteButton",
      onSelect: (row: IApiMethods) => this.requestDeleteMethod(row)
    }
  ];

  renderTable = (): React.ReactNode => {
    const methods: IApiMethods[] = this.getMethods();
    const rows: IApiMethods[] = filterMethods(methods, this.state.searchText);
    const isNoResults: boolean = methods.length > 0 && this.state.searchText.length > 0;

    return (
      <KuikaListTable<IApiMethods>
        data={rows}
        columns={this.getColumns()}
        rowActions={this.getRowActions()}
        rowActionsTestId={(row: IApiMethods) => `${row.name}methodThreeDotMenu`}
        rowKey={(row: IApiMethods) => row.id}
        revealRowKey={this.state.revealMethodId}
        onRowRevealed={(): void => this.setState({ revealMethodId: null })}
        isRowBusy={(row: IApiMethods) => row.id === this.state.busyMethodId}
        rowClassName={(row: IApiMethods) =>
          row.id === this.props.sd.activeApiMethodId ? "kuika-api-builder__row--selected" : undefined
        }
        onRowClick={(row: IApiMethods) => this.props.sd.setActiveMethod(row.id)}
        // The method glyph, not the tool's: the API mark is the letters "API", which reads
        // as a stutter above "No API Method exists."
        emptyIcon={<KuikaSvg name={isNoResults ? "search-off" : "code"} width={32} height={32} />}
        emptyTitle={isNoResults ? "No method found" : "No API Method exists."}
        emptyDescription={
          isNoResults
            ? "We couldn't find any items matching your search."
            : "Add a method to expose one of this API's operations."
        }
        data-testid="apiMethodsTable"
      />
    );
  };

  render() {
    const api: IApiManagement | undefined = this.getApi();

    return (
      <>
        <div className="kuika-api-builder__editor">
          {/* The bar renders inside the antd Form so the inline rename keeps antd's async
              validators and its error placement. It is the column's first row, OUTSIDE the
              page's gutter — the editor's chrome, which does not scroll with the list. */}
          <Form
            name="api"
            ref={this.formRef}
            className="kuika-api-builder__title-form"
            initialValues={{ name: api?.name }}
          >
            <ApiEditorHeader
              apiName={api?.name ?? ""}
              isNameOnEdit={this.state.isNameOnEdit}
              onStartRename={() => this.setState({ isNameOnEdit: true })}
              onSubmitName={(name: string) => void this.handleRenameSubmit(name)}
              onAbortRename={this.resetNameField}
              nameValidator={this.existingNameValidator}
              reservedValidator={this.reservedNameValidator}
              validateName={this.validateName}
              apis={this.getApis()}
              currentApi={api}
              onSwitchApi={this.props.onSwitchApi}
              onCreateNew={this.props.onCreateNew}
              onBack={this.props.onBack}
              rowActions={this.props.rowActions}
            />
          </Form>

          <KuikaListPage className="kuika-api-builder" data-testid="apiEditorScreen" belowBar>
            <KuikaCreateCardRow>
              <KuikaCreateCard
                title="Add a method"
                description="Expose one operation of this API — its actions, inputs and authorization are set on the right."
                icon={<KuikaSvg name="plus" width={20} height={20} />}
                iconBackground="var(--blue-1)"
                iconColor="var(--accent-fg)"
                onClick={this.openCreateMethodModal}
                loading={this.state.methodStatus === "loading" && !this.props.sd.editingApiMethodId}
                data-testid="addNewMethodButton"
              />
            </KuikaCreateCardRow>

            <KuikaListPageToolbar
              search={
                <KuikaInput
                  size="md"
                  placeholder="Search by name..."
                  value={this.state.searchText}
                  leftIcon={<KuikaSvg name="search" width={14} height={14} />}
                  onChange={(_e: React.ChangeEvent<HTMLInputElement>, value: string | number) =>
                    this.setState({ searchText: String(value) })
                  }
                  data-testid="searchApiMethodInput"
                />
              }
            />

            {this.renderTable()}
          </KuikaListPage>
        </div>

        <ApiMethodModal
          visible={this.state.isMethodModalOpen}
          editingMethod={this.getEditingMethod()}
          methods={this.getMethods()}
          status={this.state.methodStatus}
          onCancel={this.closeMethodModal}
          onSubmit={(name: string, description?: string) => void this.handleMethodSubmit(name, description)}
        />

        <DeleteModal
          itemName={this.state.methodToDelete?.name ?? ""}
          triggerModal={!!this.state.methodToDelete}
          deleteItem={() => {
            const target: IApiMethods | undefined = this.state.methodToDelete;
            if (target) void this.deleteMethod(target);
          }}
          deleteType="method"
          closeModal={() => this.setState({ methodToDelete: undefined })}
        />
      </>
    );
  }

  checkNeedRender = (_changeReason: ESdChangeReason): boolean => {
    return true;
  };
}

const apiEditor = withSD<IApiEditorProps>(ApiEditor);
export { apiEditor as ApiEditor };
